import uniq from 'lodash/uniq'
import { createSelector } from 'reselect'
import type { ItemWithUpdatedFields } from 'types/nesting'
import type { State } from 'types/redux'
import type { Track, TrackId } from 'types/track'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'
import { MAIN } from '@disco/constants/Nesting'
import type { TrackNestingState } from '@disco/state-management/reducers/nesting'
import {
  getTrack,
  getTracksByIds,
  getTracksByNestingParentId,
} from '@disco/state-management/selectors/tracks'
import {
  getStemsFromTracksArray,
  getVersionsFromTracksArray,
  mergeTracksWithUpdatedFields,
} from '@disco/utils/nesting'
import { getNestedTracksCount } from '@disco/utils/tracks'
/**
 * Gets the nesting state for a specific track ID.
 * @param state - The Redux state
 * @param id - The track ID to get nesting state for
 * @returns The track nesting state object
 */
export const getNestingById = (state: State, id: TrackId): TrackNestingState =>
  state.nesting[id]
/**
 * Gets nested stems from tracks with a specific nesting parent ID.
 * @param state - The Redux state
 * @param props - Object containing the parent ID
 * @returns Array of stem tracks
 */
export const getNestedStems = createSelector(
  getTracksByNestingParentId,
  tracks => getStemsFromTracksArray(tracks),
)
/**
 * Gets nested versions from tracks with a specific nesting parent ID.
 * @param state - The Redux state
 * @param props - Object containing the parent ID
 * @returns Array of version tracks
 */
export const getNestedVersions = createSelector(
  getTracksByNestingParentId,
  tracks => getVersionsFromTracksArray(tracks),
)
/**
 * Gets tracks with updated fields merged in.
 * @param state - The Redux state
 * @param trackIds - Array of track IDs to retrieve
 * @param updatedFields - Object mapping track IDs to updated field data
 * @returns Array of tracks with updated fields merged
 */
export const getTracksWithUpdatedFields = (
  state: State,
  trackIds: TrackId[],
  updatedFields: Record<TrackId, ItemWithUpdatedFields> | null | undefined,
): Track[] => {
  const tracks = getTracksByIds(state, {
    ids: trackIds,
  })
  if (!updatedFields) return tracks
  return mergeTracksWithUpdatedFields(tracks, updatedFields)
}
/**
 * Gets the nesting label for a track based on its nesting properties.
 * @param track - The track object
 * @returns The nesting label string, or null if no label applies
 * @todo Enable nesting - https://disco-team.atlassian.net/browse/DISCO-5771
 */
export const getNestingLabel = (track: Track): string | null | undefined => {
  let label: string | null = null

  if (track.nesting_label) {
    label = track.nesting_label
  }

  if (getNestedTracksCount(track) > 0 && !track.nesting_parent_id) {
    label = MAIN
  }

  // TODO: enable nesting
  // https://disco-team.atlassian.net/browse/DISCO-5771
  if (isAggregatedSearchTrack(track)) {
    label = null
  }

  return label
}
/**
 * Gets nested track IDs that need to be fetched (not yet loaded).
 * @param state - The Redux state
 * @param mainTrackIds - Array of main track IDs to get nested tracks for
 * @returns Array of unique nested track IDs that need to be fetched
 */
export const getNestedTracksIdsForFetch = (
  state: State,
  mainTrackIds: TrackId[],
): TrackId[] => {
  const allNestedTrackIds: TrackId[] = mainTrackIds
    .flatMap(
      id =>
        getTrack(state, {
          id,
        })?.nested_tracks?.map(t => t.id) ?? [],
    )
    .filter((id): id is TrackId => id != null)
  // we need to fetch only unique tracks that are not loaded yet
  const trackIds: TrackId[] = uniq<TrackId>(allNestedTrackIds).filter(
    id =>
      Boolean(id) &&
      !getTrack(state, {
        id,
      }),
  )
  return trackIds
}
/**
 * Gets main track IDs that have nested tracks but haven't loaded their nested_tracks yet.
 * @param state - The Redux state
 * @param mainTrackIds - Array of main track IDs to check
 * @returns Array of unique main track IDs that need nested tracks fetched
 */
export const getMainTracksIdsForFetch = (
  state: State,
  mainTrackIds: TrackId[],
): TrackId[] => {
  const allMainTrackIds = mainTrackIds.filter(id => {
    const track = getTrack(state, {
      id,
    })
    // "main" tracks from search, browse or starred panels don't contain nested_tracks
    return track && getNestedTracksCount(track) > 0 && !track.nested_tracks
  })
  return uniq(allMainTrackIds)
}

export const getNestedTracksIds = (state: State) => {
  return Object.keys(state.nesting)
}
